import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { BookMarked, Trophy, XCircle } from 'lucide-react';
import { useSeriesProgress } from '@/hooks/useSeriesProgress';
import { SeriesTrackerCard } from '@/components/SeriesTrackerCard';
import { EmptyState } from '@/components/EmptyState';
import { MetaTags } from '@/components/MetaTags';

export default function SeriesTracker() {
  const [activeTab, setActiveTab] = useState('active');
  const {
    inProgressSeries,
    completedSeries,
    dismissedSeries,
    isLoading,
  } = useSeriesProgress();

  return (
    <>
      <MetaTags
        title="Series Tracker"
        description="Track your reading progress across book series and never lose your place"
      />
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="container mx-auto px-4 py-8 max-w-6xl space-y-6 w-full">
        {/* Info Banner */}
        {inProgressSeries.length === 0 && completedSeries.length === 0 && !isLoading && (
          <Card className="bg-muted/50 border-dashed">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div>
                  <p className="font-medium">No Series Tracked Yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    To track series, add books from a series to your library and mark them as read or reading.
                    Series progress is automatically calculated from your library.
                  </p>
                </div>
                <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
                  <p>📚 <strong>How it works:</strong> Search for books in series (e.g., Harry Potter, Mistborn), add them to your library, and mark your reading status.</p>
                  <p>⚡ <strong>Automatic:</strong> Your progress is derived directly from your library — no manual tracking needed.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="card-elevated">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <BookMarked className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{inProgressSeries.length}</p>
                    <p className="text-sm text-muted-foreground">In Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-green-500/10">
                    <Trophy className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{completedSeries.length}</p>
                    <p className="text-sm text-muted-foreground">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-muted">
                    <XCircle className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dismissedSeries.length}</p>
                    <p className="text-sm text-muted-foreground">Dismissed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Series Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="active">
                In Progress ({inProgressSeries.length})
              </TabsTrigger>
              <TabsTrigger value="completed">
                Completed ({completedSeries.length})
              </TabsTrigger>
              <TabsTrigger value="dismissed">
                Dismissed ({dismissedSeries.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="space-y-4 mt-6">
              {isLoading ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {[1, 2].map((i) => (
                    <Card key={i} className="card-elevated animate-pulse">
                      <CardContent className="h-64" />
                    </Card>
                  ))}
                </div>
              ) : inProgressSeries.length === 0 ? (
                <EmptyState
                  icon={BookMarked}
                  title="No series in progress"
                  description="Start reading books from a series to track your progress here"
                />
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {inProgressSeries.map((series) => (
                    <SeriesTrackerCard key={series.key} series={series} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="completed" className="space-y-4 mt-6">
              {isLoading ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {[1, 2].map((i) => (
                    <Card key={i} className="card-elevated animate-pulse">
                      <CardContent className="h-64" />
                    </Card>
                  ))}
                </div>
              ) : completedSeries.length === 0 ? (
                <EmptyState
                  icon={Trophy}
                  title="No completed series yet"
                  description="Finish reading all books in a series to see it here"
                />
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {completedSeries.map((series) => (
                    <SeriesTrackerCard
                      key={series.key}
                      series={series}
                      showDismissButton={false}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="dismissed" className="space-y-4 mt-6">
              {isLoading ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {[1, 2].map((i) => (
                    <Card key={i} className="card-elevated animate-pulse">
                      <CardContent className="h-64" />
                    </Card>
                  ))}
                </div>
              ) : dismissedSeries.length === 0 ? (
                <EmptyState
                  icon={XCircle}
                  title="No dismissed series"
                  description="Series you choose not to continue will appear here"
                />
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {dismissedSeries.map((series) => (
                    <SeriesTrackerCard
                      key={series.key}
                      series={series}
                      showDismissButton={false}
                      showReactivateButton
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}
